/**
 * Welcome Agent
 *
 * Responsible ONLY for saying the welcome message in the detected language.
 * The backend has NO tools for this agent — the AgentManager listens for
 * `turncomplete` and immediately transitions to `router_agent` once the AI
 * finishes speaking.  A safety-fallback timer is kept in case turncomplete
 * never fires (e.g. muted speaker / audio error).
 */

import { AgentNode, AgentContext, ToolCallResult, VoiceGender } from '../types';
import { requireLanguage, languageDetectedGuardrail } from '../guardrails';

export function createWelcomeAgent(voiceGender: VoiceGender = 'male'): AgentNode {
  // Safety-fallback timer — fires only if turncomplete never fires
  let fallbackTimer: number | null = null;

  return {
    id: 'welcome_agent',
    name: 'Welcome Agent',
    description: 'Says welcome message after language detection, then auto-transitions',

    initialPrompt: 'Say the welcome message in the user\'s language now. Then STOP.',

    entryGuardrails: requireLanguage(),

    exitGuardrails: {
      transitions: new Map([
        ['welcome_complete', {
          target: { agentId: 'router_agent' },
          guardrails: [languageDetectedGuardrail],
        }],
      ]),
      fallback: undefined,
    },

    // No tool calls expected — backend has no tools for this agent.
    // If the AI somehow has a stale session with proceed_to_menu or any other tool,
    // treat ANY tool call as a welcome_complete and silently transition to router_agent.
    handleToolCall: async (
      toolName: string,
      args: Record<string, any>,
      context: AgentContext
    ): Promise<ToolCallResult> => {
      console.warn('[WelcomeAgent] Unexpected tool call — converting to silent router transition:', toolName);
      return {
        toolName,
        args,
        result: { success: true, message: 'Welcome complete — transitioning to menu' },
        triggersTransition: true,
        transitionTo: 'router_agent',
        transitionData: { language: context.transitionData.language },
      };
    },

    onActivate: async (context: AgentContext) => {
      console.log('[WelcomeAgent] Activated with language:', context.transitionData.language);

      // Safety fallback: if turncomplete never fires within 8 s, dispatch auto-transition
      fallbackTimer = window.setTimeout(() => {
        console.warn('[WelcomeAgent] Fallback timer fired — turncomplete did not arrive in time');
        window.dispatchEvent(new CustomEvent('agent:auto-transition', {
          detail: { fromAgent: 'welcome_agent', toAgent: 'router_agent' },
        }));
        fallbackTimer = null;
      }, 8000);
    },

    onDeactivate: async (_context: AgentContext) => {
      if (fallbackTimer) {
        clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }
      console.log('[WelcomeAgent] Deactivated');
    },
  };
}

export default createWelcomeAgent;
